/**
 * @file unitChain.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-28
 * @description The unit chain (Cam, 28 Oct 2026 — "if there is a mismatch
 *              anywhere along the line (from SO to PO to bill to sales invoice
 *              to cost price to sale price etc.) then please flag this via an
 *              icon with tooltip explainer"). ONE rule: given a line's units
 *              along the chain, what does not agree, each in plain words.
 *
 *   The chain, for one customer order line:
 *     order    the order line — its own unit (a weight, or None = 0), else
 *              its item's, else a tonne for a bulk sales code; none at all is
 *              itself a flag (units are required now — utils/lineUnitRequired);
 *     po       the PO line it is bought on — the same unit as the order line;
 *     bills    each bill line for it — a bill per tonne, per kg or per pack
 *              against the PO line's unit is fine ONLY when it was converted
 *              (utils/billUnitConversion found the way, or the bill line was
 *              put in the PO's units);
 *     invoice  the sales invoice line — the same unit as the order line;
 *     cost     the cost figure offered (proposed / confirmed): converted to
 *              the line's unit (an item's cost, a price change, a bill) is
 *              fine; one per another unit (a contract's, per the item's unit)
 *              is not; an item cost with no unit is said;
 *     sale     the sale price offered: a contract's / monthly / mineral list
 *              price is per the item's unit — said when the line counts in
 *              another.
 *   Only what is KNOWN on both sides is compared — an unknown is never
 *   guessed into a mismatch. Rare once units are required and the clean-up
 *   report has been worked through.
 *
 *   The server's utils/unitChain.js is GENERATED from this file by
 *   scripts/sync-shared-utils.js — edit this one and re-run it.
 */

import { formatCount, formatQuantity } from './numberFormatters';

const BULK_CODES = ['201', '202', '204', '206', '209'];

const blank = (v) => v === '' || v === null || v === undefined || String(v).trim() === '';
const num = (v) => (blank(v) ? null : (Number.isFinite(Number(v)) ? Number(v) : null));

/**
 * The unit one quantity counts in, kg: its own (0 = None), else the item's,
 * else a tonne for a bulk sales code, else null (unknown).
 */
export function effectiveKg(ownKg, itemKg = null, salesCode = null) {
  const own = num(ownKg);
  if (own !== null && own >= 0) return own;
  const item = num(itemKg);
  if (item !== null && item > 0) return item;
  if (BULK_CODES.includes(String(salesCode || '').trim().toUpperCase())) return 1000;
  return null;
}

/** 'none' (no weight), 't' (tonnes), 'pack' (anything else known), null (unknown). */
export function unitKind(kg) {
  const n = num(kg);
  if (n === null) return null;
  if (n === 0) return 'none';
  return n === 1000 ? 't' : 'pack';
}

const sameUnit = (a, b) => num(a) !== null && num(b) !== null && Math.abs(num(a) - num(b)) < 0.0001;

/** "tonnes", "25kg bags", "500kg totes", "12.5kg packs", "no weight". */
export function unitPlural(kg) {
  const n = num(kg);
  if (n === null) return 'no known unit';
  if (n === 0) return 'no weight (None)';
  if (n === 1000) return 'tonnes';
  if (n === 500) return '500kg totes';
  if (n === 25 || n === 20) return `${n}kg bags`;
  return `${Number(n.toFixed(3))}kg packs`;
}

/** "a tonne", "a 25kg bag", "a 500kg tote", "a 12.5kg pack". */
export function unitSingular(kg) {
  const n = num(kg);
  if (n === null || n === 0) return 'unit';
  if (n === 1000) return 'tonne';
  if (n === 500) return '500kg tote';
  if (n === 25 || n === 20) return `${n}kg bag`;
  return `${Number(n.toFixed(3))}kg pack`;
}

/** "counts 40 × 25kg bag", "counts 1.00 T", "counts in 25kg bags" (no quantity). */
export function countsPhrase(kg, qty = null) {
  const n = num(kg);
  const q = num(qty);
  if (q === null) return `counts in ${unitPlural(n)}`;
  if (n === 1000) return `counts ${formatQuantity(q)} T`;
  if (n === 0) return `counts ${formatCount(q)} (no weight)`;
  return `counts ${formatCount(q)} × ${unitSingular(n)}`;
}

const BILL_UNIT_WORDS = { kg: 'per kg', t: 'per tonne', pack: 'per pack' };

/**
 * What does not agree along one line's chain.
 * @param {object} c
 * @param {{ ref, ownKg, itemKg, salesCode, qty }} c.order
 * @param {{ ref, ownKg, itemKg, qty }|null} c.po
 * @param {Array<{ ref, unit: 'kg'|'t'|'pack'|'other'|null, converted: boolean }>} c.bills
 * @param {{ ref, ownKg, itemKg, qty }|null} c.invoice
 * @param {{ label, kg, unitless }|null} c.cost   kg = the unit the figure is per when it is NOT
 *        converted to the line's; unitless = an item cost with no unit
 * @param {{ label, kg }|null} c.sale             kg = the unit the price is per (the item's)
 * @returns {Array<{ key, text }>}
 */
export function unitChainFlags(c = {}) {
  const out = [];
  const order = c.order || null;
  const orderKg = order ? effectiveKg(order.ownKg, order.itemKg, order.salesCode) : null;
  const orderRef = order?.ref || 'The order line';
  if (order && orderKg === null) {
    out.push({ key: 'no_unit', text: `${orderRef}'s line has no unit — its quantity could be bags or tonnes. Set its unit on the order.` });
  }
  const po = c.po || null;
  const poKg = po ? effectiveKg(po.ownKg, po.itemKg ?? order?.itemKg, order?.salesCode) : null;
  if (po && orderKg !== null && poKg !== null && !sameUnit(orderKg, poKg)) {
    out.push({ key: 'po', text: `${orderRef} ${countsPhrase(orderKg, order.qty)}; ${po.ref} ${countsPhrase(poKg, po.qty)} — they should count the same.` });
  }
  // Bills against the PO line's unit (else the order line's).
  const buyKg = poKg ?? orderKg;
  for (const b of (Array.isArray(c.bills) ? c.bills : [])) {
    const bu = BILL_UNIT_WORDS[b?.unit] ? b.unit : null;
    const kind = unitKind(buyKg);
    if (!bu || !kind || kind === 'none' || b.converted) continue;
    if (bu === kind) continue;
    out.push({
      key: `bill:${b.ref}`,
      text: `${po?.ref || orderRef} ${countsPhrase(buyKg, po?.qty ?? order?.qty)}; its bill ${b.ref} is ${BILL_UNIT_WORDS[bu]} and wasn't converted.`
    });
  }
  const inv = c.invoice || null;
  const invKg = inv ? effectiveKg(inv.ownKg, inv.itemKg ?? order?.itemKg, order?.salesCode) : null;
  if (inv && orderKg !== null && invKg !== null && !sameUnit(orderKg, invKg)) {
    out.push({ key: 'invoice', text: `${inv.ref} ${countsPhrase(invKg, inv.qty)}; ${orderRef} ${countsPhrase(orderKg, order.qty)}.` });
  }
  const lineKg = orderKg ?? poKg;
  const cost = c.cost || null;
  if (cost && cost.unitless) {
    out.push({ key: 'cost_unitless', text: `The item's cost has no unit — nobody knows if it is per bag or per tonne. Set the item's unit (or the line's cost by hand).` });
  } else if (cost && lineKg !== null && num(cost.kg) !== null && unitKind(lineKg) !== 'none' && !sameUnit(cost.kg, lineKg)) {
    out.push({ key: 'cost', text: `The cost (${cost.label}) is per ${unitSingular(cost.kg)}; this line counts in ${unitPlural(lineKg)}.` });
  }
  const sale = c.sale || null;
  if (sale && lineKg !== null && num(sale.kg) !== null && unitKind(lineKg) !== 'none' && !sameUnit(sale.kg, lineKg)) {
    out.push({ key: 'sale', text: `The price (${sale.label}) is per ${unitSingular(sale.kg)} — the item's unit; this line counts in ${unitPlural(lineKg)}.` });
  }
  return out;
}

/** The icon's tooltip: one line per flag. */
export function unitFlagsTooltip(flags) {
  if (!flags || !flags.length) return '';
  return ['Units don\'t agree:', ...flags.map(f => `• ${f.text}`)].join('\n');
}
